<template>
    <form class="form-shell" @submit.prevent="submitForm">
        <div class="form-body">
        <!-- Basic Information Section -->
        <section class="form-section">
            <div class="section-header section-header-with-meta">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'building']" class="text-lg" />
                </div>
                <div class="section-heading-text">
                    <h2 class="section-title">Basic Information</h2>
                    <p class="section-subtitle">Partner identity, classification, and entity coverage.</p>
                </div>
            </div>

            <div class="logo-upload-card">
                <div v-if="logoPreview" class="logo-preview-wrapper">
                    <div @click.stop="cancelUploadedPhoto" class="logo-remove-btn">
                        <icon-close class="h-3.5 w-3.5"></icon-close>
                    </div>
                    <img class="logo-preview-img" :src="logoPreview" alt="Logo preview">
                </div>
                <div v-else class="logo-placeholder">
                    <div class="logo-placeholder-icon">
                        <font-awesome-icon :icon="['fas', 'image']" class="text-4xl text-slate-400" />
                    </div>
                    <p class="logo-placeholder-text">Partner logo will appear here</p>
                </div>
                <input ref="logoPicture" class="hidden" id="profile-picture" @change="handleUploadedPhoto" type="file"
                    accept="image/*">
                <button type="button" @click.stop="onPickFile('logoPicture')"
                    :class="['logo-upload-btn', logoPicture.isValid ? '' : 'logo-upload-btn-error']">
                    <icon-plus class="h-4 w-4"></icon-plus>
                    <span>Upload Logo</span>
                    <span class="logo-upload-hint">JPG, JPEG, PNG · max 2MB</span>
                </button>
                <transition name="fade" mode="out-in">
                    <p v-if="!logoPicture.isValid" class="form-error-msg">{{ logoPicture.msg }}</p>
                </transition>
            </div>

            <div class="form-group landing-page-display-group">
                <label class="form-label">Landing Page Visibility</label>
                <label class="landing-page-display-option">
                    <input type="checkbox" v-model="showOnLandingPage" class="landing-page-display-checkbox" />
                    <span class="landing-page-display-text">Display logo in the "Our Partners" section on the landing page</span>
                </label>
            </div>

            <div class="form-grid">
                <div class="form-group" :class="{ 'form-group-error': !name.isValid }">
                    <label for="name" class="form-label">Partner Name <span class="required">*</span></label>
                    <input type="text" id="name" v-model.trim="name.val" placeholder="Enter partner name"
                        class="form-input" @blur="clearValidity('name')" />
                    <transition name="fade" mode="out-in">
                        <p v-if="!name.isValid" class="form-error-msg">{{ name.msg }}</p>
                    </transition>
                </div>
                <div class="form-group">
                    <label for="location" class="form-label">Location</label>
                    <input type="text" id="location" v-model.trim="location.val"
                        placeholder="City, country, or address" class="form-input" />
                </div>
                <div class="form-group" :class="{ 'form-group-error': !industry.isValid }">
                    <label for="industry" class="form-label">Industry <span class="required">*</span></label>
                    <select id="industry" v-model="industry.val" class="form-input form-select"
                        @change="onIndustryChange">
                        <option value="">Select industry</option>
                        <option v-for="ind in industryOptions" :key="ind.id" :value="ind.id">{{ ind.name }}</option>
                        <option value="other">Other (enter manually)</option>
                    </select>
                    <transition name="fade" mode="out-in">
                        <p v-if="!industry.isValid" class="form-error-msg">{{ industry.msg }}</p>
                    </transition>
                </div>
                <div v-if="industry.val === 'other'" class="form-group" :class="{ 'form-group-error': !industryOther.isValid }">
                    <label for="industry-other" class="form-label">Enter Industry Name <span class="required">*</span></label>
                    <input type="text" id="industry-other" v-model.trim="industryOther.val"
                        placeholder="Enter new industry name" class="form-input"
                        @blur="validateIndustryOther" />
                    <transition name="fade" mode="out-in">
                        <p v-if="!industryOther.isValid" class="form-error-msg">{{ industryOther.msg }}</p>
                    </transition>
                </div>
                <div class="form-group">
                    <label for="type-of-collaboration" class="form-label">Type of Collaboration</label>
                    <select id="type-of-collaboration" v-model="selectedTypeOfCollaboration"
                        class="form-input form-select">
                        <option value="">Select type</option>
                        <option v-for="opt in typeOfCollaborationOptions" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                </div>
                <div class="form-group form-group-multiselect">
                    <label class="form-label">Classifications</label>
                    <multiselect v-model="selectedClassifications" :options="classificationOptions" :multiple="true"
                        :close-on-select="false" placeholder="Select classifications" class="form-multiselect">
                    </multiselect>
                </div>
            </div>

            <div class="form-group mt-4">
                <label class="form-label">Is this company part of a group/mother company?</label>
                <select v-model="isPartOfGroupCompany" class="form-input form-select">
                    <option value="">No</option>
                    <option value="yes">Yes</option>
                </select>
            </div>
            <div v-if="isPartOfGroupCompany === 'yes'" class="form-group mt-4">
                <label class="form-label">Select Group/Mother Company</label>
                <multiselect v-model="selectedParentPartner" :options="partnersForSelect" :multiple="false"
                    :close-on-select="true" placeholder="Select partner" label="name" track-by="id">
                    <template #singleLabel="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                            <span class="option__small">({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                        </span>
                    </template>
                    <template #option="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                            <span class="option__small">({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                        </span>
                    </template>
                </multiselect>
            </div>

            <div class="form-group entity-group" :class="{ 'form-group-error': !entity.isValid }">
                <div class="entity-panel">
                    <div class="entity-panel-header">
                        <div class="entity-panel-heading">
                            <label class="form-label entity-panel-label">
                                Entity <span class="required">*</span>
                            </label>
                            <p class="entity-panel-hint">
                                Select one or more entities covered by this partnership.
                            </p>
                        </div>
                        <span v-if="selectedEntities.length" class="entity-selected-count">
                            {{ selectedEntities.length }} selected
                        </span>
                    </div>

                    <div class="entity-chip-grid" role="group" aria-label="Entity selection">
                        <label
                            v-for="(ent, index) in entityList"
                            :key="'entity-' + index"
                            class="entity-chip"
                            :class="{ 'entity-chip--selected': selectedEntities.includes(ent) }"
                        >
                            <input
                                type="checkbox"
                                v-model="selectedEntities"
                                :value="ent"
                                class="entity-chip-input"
                                @change="clearValidity('entity')"
                            />
                            <span class="entity-chip-check" aria-hidden="true">
                                <font-awesome-icon
                                    v-if="selectedEntities.includes(ent)"
                                    :icon="['fas', 'check']"
                                />
                            </span>
                            <span class="entity-chip-name">{{ getEntityName(ent) }}</span>
                        </label>
                    </div>

                    <div v-if="selectedEntities.length" class="entity-summary">
                        <span class="entity-summary-label">Selected:</span>
                        <span
                            v-for="(ent, idx) in selectedEntities"
                            :key="'sel-' + idx"
                            class="entity-summary-tag"
                        >
                            {{ getEntityName(ent) }}
                        </span>
                    </div>
                </div>
                <transition name="fade" mode="out-in">
                    <p v-if="!entity.isValid" class="form-error-msg">{{ entity.msg }}</p>
                </transition>
            </div>
        </section>

        <!-- Contact Person Section -->
        <section class="form-section">
            <div class="section-header section-header-with-meta">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'users']" class="text-lg" />
                </div>
                <div class="section-heading-text">
                    <div class="section-title-row">
                        <h2 class="section-title">Contact Person</h2>
                        <span v-if="inputContactPerson.length" class="section-count">{{ inputContactPerson.length }}</span>
                    </div>
                    <p class="section-subtitle">Primary points of contact for this partnership.</p>
                </div>
            </div>

            <div class="repeatable-list">
                <article
                    v-for="(item, index) in inputContactPerson"
                    :key="'cp-' + index"
                    class="repeatable-card"
                >
                    <header class="repeatable-card-header">
                        <div class="repeatable-card-header-left">
                            <span class="repeatable-badge">{{ index + 1 }}</span>
                            <span class="repeatable-card-label">Contact {{ index + 1 }}</span>
                        </div>
                        <button
                            v-if="inputContactPerson.length > 1"
                            type="button"
                            class="repeatable-btn-remove"
                            title="Remove contact"
                            @click.stop="removeContactPerson(index)"
                        >
                            <font-awesome-icon :icon="['fas', 'trash-alt']" class="repeatable-btn-remove-icon" />
                            <span>Remove</span>
                        </button>
                    </header>
                    <div class="repeatable-card-body contact-person-fields">
                        <div class="form-group">
                            <label :for="`cp-name-${index + 1}`" class="form-label">Name <span class="required">*</span></label>
                            <input type="text" :id="`cp-name-${index + 1}`" v-model.trim="item.name" placeholder="Full name"
                                class="form-input" />
                        </div>
                        <div class="form-group">
                            <label :for="`cp-phone-${index + 1}`" class="form-label">Phone <span class="required">*</span></label>
                            <input type="text" :id="`cp-phone-${index + 1}`" v-model.trim="item.phone"
                                placeholder="Phone number" class="form-input" @keypress="allowNumbersOnly" />
                        </div>
                        <div class="form-group">
                            <label :for="`cp-email-${index + 1}`" class="form-label">Email <span class="required">*</span></label>
                            <input type="text" :id="`cp-email-${index + 1}`" v-model.trim="item.email"
                                placeholder="email@example.com" class="form-input" />
                        </div>
                        <div class="form-group">
                            <label :for="`cp-designation-${index + 1}`" class="form-label">Designation <span class="required">*</span></label>
                            <input type="text" :id="`cp-designation-${index + 1}`" v-model.trim="item.designation"
                                placeholder="Job title" class="form-input" />
                        </div>
                    </div>
                </article>
                <div class="repeatable-footer">
                    <button type="button" class="repeatable-btn-add" @click.stop="addMoreContactPerson">
                        <icon-plus class="h-4 w-4"></icon-plus>
                        <span>Add another contact</span>
                    </button>
                </div>
            </div>
            <transition name="form-fade" mode="out-in">
                <p v-if="contactPersonErrorMsg" class="form-error-msg form-error-msg-center">{{ contactPersonErrorMsg }}
                </p>
            </transition>
        </section>

        <!-- Joint Committee Members -->
        <joint-committee-members-editor
          v-model="inputJointCommitteeMembers"
          :error-message="jointCommitteeErrorMsg"
        ></joint-committee-members-editor>

        <!-- Account Managers -->
        <account-managers-editor
          v-model="inputAccountManagers"
          :error-message="accountManagersErrorMsg"
        ></account-managers-editor>

        <!-- Upload Documents Section -->
        <section class="form-section">
            <div class="section-header section-header-with-meta">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'paperclip']" class="text-lg" />
                </div>
                <div class="section-heading-text">
                    <div class="section-title-row">
                        <h2 class="section-title">Upload Documents</h2>
                        <span v-if="inputAttachments.length" class="section-count">{{ inputAttachments.length }}</span>
                    </div>
                    <p class="section-subtitle">Optional. Agreements, approvals, and supporting files.</p>
                </div>
            </div>

            <div v-if="!inputAttachments.length" class="docs-empty">
                <div class="docs-empty-icon">
                    <font-awesome-icon :icon="['fas', 'paperclip']" />
                </div>
                <h3 class="docs-empty-title">No documents yet</h3>
                <p class="docs-empty-text">
                    Attach supporting files if needed. You can submit without documents.
                </p>
                <button type="button" class="docs-btn-primary" @click.stop="addFirstAttachment">
                    <icon-plus class="h-4 w-4"></icon-plus>
                    <span>Add document</span>
                </button>
            </div>

            <div v-else class="repeatable-list">
                <article
                    v-for="(item, index) in inputAttachments"
                    :key="'doc-' + index"
                    class="repeatable-card"
                >
                    <header class="repeatable-card-header">
                        <div class="repeatable-card-header-left">
                            <span class="repeatable-badge">{{ index + 1 }}</span>
                            <span class="repeatable-card-label">Document {{ index + 1 }}</span>
                        </div>
                        <button
                            type="button"
                            class="repeatable-btn-remove"
                            title="Remove document"
                            @click.stop="removeAttachment(index)"
                        >
                            <font-awesome-icon :icon="['fas', 'trash-alt']" class="repeatable-btn-remove-icon" />
                            <span>Remove</span>
                        </button>
                    </header>
                    <div class="repeatable-card-body document-fields">
                        <div class="form-group">
                            <label :for="`cp-at-name-${index + 1}`" class="form-label">Document Name</label>
                            <input type="text" :id="`cp-at-name-${index + 1}`" v-model.trim="item.file_name"
                                placeholder="e.g. Partnership Agreement" class="form-input" :disabled="!item.is_new"
                                :class="{ 'form-input-disabled': !item.is_new }" />
                        </div>
                        <div class="form-group">
                            <div v-if="!item.is_new" class="document-existing">
                                <icon-paper-clip class="h-4 w-4 text-slate-500 flex-shrink-0"></icon-paper-clip>
                                <a :href="getSrcFile(item.path)" target="_blank" class="document-link">{{
                                    item.original_filename }}</a>
                                <button type="button" @click.stop="removeAttachedFile(index)" class="document-remove-btn">
                                    <icon-close class="h-4 w-4"></icon-close>
                                </button>
                            </div>
                            <div v-else>
                                <label :for="`document-file-${index}`" class="form-label">File</label>
                                <div class="file-input-wrapper">
                                    <input :id="`document-file-${index}`" :key="`document-file-${index}`" type="file"
                                        class="file-input" @change="handleFileChange"
                                        accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" />
                                    <span class="file-input-hint">JPG, PNG, PDF, DOC, PPT · max 16MB</span>
                                </div>
                                <transition name="fade" mode="out-in">
                                    <p v-if="!item.document.isValid" class="form-error-msg">{{ item.document.msg }}</p>
                                </transition>
                            </div>
                        </div>
                    </div>
                </article>
                <div class="repeatable-footer">
                    <button type="button" class="repeatable-btn-add" @click.stop="addMoreAttachment">
                        <icon-plus class="h-4 w-4"></icon-plus>
                        <span>Add another document</span>
                    </button>
                </div>
            </div>
            <transition name="form-fade" mode="out-in">
                <p v-if="attachmentErrorMsg" class="form-error-msg form-error-msg-center">{{ attachmentErrorMsg }}</p>
            </transition>
        </section>
        </div>

        <!-- Sticky action bar -->
        <div class="form-footer">
            <div class="form-footer-inner">
                <div v-if="isLoading" class="form-footer-loading">
                    <base-spinner-with-text class-list="h-6 w-6 text-ternary" :text="getLoadingBtnText"
                        text-class-list="text-ternary font-medium"></base-spinner-with-text>
                </div>
                <template v-else>
                    <button type="button" class="cancel-btn" @click.stop="$emit('close')">
                        Cancel
                    </button>
                    <button type="submit" class="submit-btn">
                        {{ getBtnText }}
                    </button>
                </template>
            </div>
        </div>
    </form>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconClose from "../../components/icons/IconClose.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconPaperClip from "../../components/icons/IconPaperClip.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import JointCommitteeMembersEditor from "./JointCommitteeMembersEditor.vue";
import AccountManagersEditor from "./AccountManagersEditor.vue";
export default {
    name: "current-partners-form",
    emits: ["created", "updated", "close"],
    props: ["mode", "selectedItem", "entities"],
    components: {
        IconClose,
        IconPlus,
        IconPaperClip,
        Multiselect,
        JointCommitteeMembersEditor: JointCommitteeMembersEditor,
        AccountManagersEditor: AccountManagersEditor,
    },
    data() {
        return {
            entityList: this.entities.map(item => item.entity),
            logoPicture: {
                val: null,
                isValid: true,
                msg: "",
            },
            logoPreview: null,
            name: {
                val: "",
                isValid: true,
                msg: "",
            },
            location: {
                val: "",
            },
            industry: {
                val: "",
                isValid: true,
                msg: "",
            },
            industryOther: {
                val: "",
                isValid: true,
                msg: "",
            },
            industryOptions: [],
            entity: {
                val: "",
                isValid: true,
                msg: "",
            },
            inputContactPerson: [],
            contactPersonIndex: 0,
            contactPersonErrorMsg: "",
            inputJointCommitteeMembers: [],
            jointCommitteeErrorMsg: "",
            inputAccountManagers: [],
            accountManagersErrorMsg: "",
            inputAttachments: [],
            attachmentIndex: 0,
            attachmentErrorMsg: "",
            deletedAttachmentIds: [],
            formIsValid: true,
            isLoading: false,
            selectedEntities: [],
            selectedTypeOfCollaboration: "",
            typeOfCollaborationOptions: ["NDA", "MOU", "Agreement", "Contract"],
            selectedClassifications: [],
            classificationOptions: [
                "Employment-focused",
                "Training-focused",
                "Research-focused",
                "Sponsorship-focused",
                "Project-focused",
                "General partnership",
                "Shelved (to be upgraded or discontinued)",
            ],
            isPartOfGroupCompany: "",
            selectedParentPartner: null,
            partnersForSelect: [],
            showOnLandingPage: true,
        };
    },
    apollo: {
        getAllPartnersForSelect: {
            query: gql`
                query GetAllPartnersForSelect($exclude_partner_id: Int) {
                    getAllPartnersForSelect(exclude_partner_id: $exclude_partner_id) {
                        error
                        message
                        data {
                            id
                            name
                            token
                            is_pipeline
                        }
                    }
                }
            `,
            variables() {
                return {
                    exclude_partner_id: this.mode === 'edit' && this.selectedItem && this.selectedItem.id
                        ? parseInt(this.selectedItem.id, 10)
                        : null,
                };
            },
            skip() {
                return false;
            },
            result({ data }) {
                if (data && data.getAllPartnersForSelect && !data.getAllPartnersForSelect.error && data.getAllPartnersForSelect.data) {
                    this.partnersForSelect = data.getAllPartnersForSelect.data;
                }
            },
        },
    },
    watch: {
        partnersForSelect(newVal) {
            if (this.mode === 'edit' && this.selectedItem && this.selectedItem.parent_partner_id && newVal.length > 0 && !this.selectedParentPartner) {
                const found = newVal.find(p => p.id === this.selectedItem.parent_partner_id);
                if (found) {
                    this.selectedParentPartner = found;
                }
            }
        },
    },
    created() {
        if (this.mode == 'edit') {
            this.setFormData();
        } else {
            this.initializeVariables();
        }
    },
    mounted() {
        this.fetchPartnersForSelect();
        this.fetchIndustryOptions();
    },
    computed: {
        getBtnText() {
            if (this.mode == 'create') {
                return "Create Partner";
            } else {
                return "Save Changes";
            }
        },
        getLoadingBtnText() {
            if (this.mode == 'create') {
                return "Creating partner…";
            } else {
                return "Saving changes…";
            }
        },
    },
    methods: {
        isValidFileType(file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            const validPdfType = 'application/pdf';
            const validDocTypes = [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            const validPptTypes = [
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.ms-powerpoint.presentation.macroEnabled.12'
            ];

            const fileType = file.type.toLowerCase();

            return validImageTypes.includes(fileType) ||
                fileType === validPdfType ||
                validDocTypes.includes(fileType) ||
                validPptTypes.includes(fileType) ||
                file.name.toLowerCase().endsWith('.doc') ||
                file.name.toLowerCase().endsWith('.docx') ||
                file.name.toLowerCase().endsWith('.ppt') ||
                file.name.toLowerCase().endsWith('.pptx');
        },
        getSrcFile(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        async fetchPartnersForSelect() {
            try {
                const excludeId = this.mode === 'edit' && this.selectedItem && this.selectedItem.id
                    ? parseInt(this.selectedItem.id, 10)
                    : null;
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetAllPartnersForSelect($exclude_partner_id: Int) {
                            getAllPartnersForSelect(exclude_partner_id: $exclude_partner_id) {
                                error
                                message
                                data {
                                    id
                                    name
                                    token
                                    is_pipeline
                                }
                            }
                        }
                    `,
                    variables: { exclude_partner_id: excludeId },
                    fetchPolicy: 'network-only',
                });
                if (data && data.getAllPartnersForSelect && !data.getAllPartnersForSelect.error && data.getAllPartnersForSelect.data) {
                    this.partnersForSelect = data.getAllPartnersForSelect.data;
                }
            } catch (err) {
                console.error('Failed to fetch partners for select:', err);
            }
        },
        async fetchIndustryOptions() {
            try {
                var result = await this.$apollo.query({
                    query: gql`
                        query AllIndustries {
                            allIndustries {
                                id
                                name
                            }
                        }
                    `,
                    fetchPolicy: 'network-only',
                });
                if (result && result.data && result.data.allIndustries) {
                    this.industryOptions = result.data.allIndustries;
                }
            } catch (err) {
                console.error('Failed to fetch industries:', err);
            }
        },
        onIndustryChange() {
            this.clearValidity('industry');
            if (this.industry.val !== 'other') {
                this.industryOther.val = '';
                this.industryOther.isValid = true;
                this.industryOther.msg = '';
            }
        },
        async validateIndustryOther() {
            var name = this.industryOther.val;
            if (!name) {
                return;
            }
            try {
                var result = await this.$apollo.query({
                    query: gql`
                        query ValidateIndustryName($name: String!) {
                            validateIndustryName(name: $name) {
                                valid
                                message
                                suggestion {
                                    id
                                    name
                                }
                            }
                        }
                    `,
                    variables: { name: name },
                    fetchPolicy: 'network-only',
                });
                var payload = result && result.data ? result.data.validateIndustryName : null;
                if (payload && !payload.valid) {
                    this.industryOther.isValid = false;
                    this.industryOther.msg = payload.message || 'This name is too similar to an existing industry.';
                } else {
                    this.industryOther.isValid = true;
                    this.industryOther.msg = '';
                }
            } catch (err) {
                console.error('Failed to validate industry name:', err);
            }
        },
        allowNumbersOnly(event) {
            const charCode = event.which || event.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                event.preventDefault();
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        createEmptyAttachment() {
            return {
                is_new: true,
                id: null,
                original_filename: "",
                path: "",
                file_name: "",
                document: {
                    val: null,
                    isValid: true,
                    msg: "",
                },
            };
        },
        validateAttachment() {
            this.attachmentErrorMsg = "";
            if (!this.inputAttachments.length) {
                return true;
            }
            var last = this.inputAttachments[this.inputAttachments.length - 1];
            if (!last) {
                return false;
            }
            if (last.is_new) {
                return !!(last.file_name && last.document && last.document.val);
            }
            return !!last.file_name;
        },
        addFirstAttachment() {
            this.attachmentErrorMsg = "";
            this.inputAttachments.push(this.createEmptyAttachment());
            this.attachmentIndex = 0;
        },
        addMoreAttachment() {
            if (!this.inputAttachments.length) {
                this.addFirstAttachment();
                return;
            }
            const isValid = this.validateAttachment();
            if (isValid) {
                this.inputAttachments.push(this.createEmptyAttachment());
                this.attachmentIndex = this.inputAttachments.length - 1;
            } else {
                this.attachmentErrorMsg = "You must upload a file and document name must not be empty.";
            }
        },
        removeAttachment(index) {
            var row = this.inputAttachments[index];
            if (this.mode == "edit" && row && row.id) {
                this.deletedAttachmentIds.push(parseInt(row.id, 10));
            }
            this.inputAttachments.splice(index, 1);
            this.attachmentIndex = this.inputAttachments.length > 0
                ? this.inputAttachments.length - 1
                : -1;
            this.attachmentErrorMsg = "";
        },
        removeAttachedFile(index) {
            this.deletedAttachmentIds.push(parseInt(this.inputAttachments[index].id));
            this.inputAttachments[index].id = null;
            this.inputAttachments[index].file_name = "";
            this.inputAttachments[index].is_new = true;
        },
        validateContactPerson() {
            this.contactPersonErrorMsg = "";
            if (this.inputContactPerson[this.contactPersonIndex].name && this.inputContactPerson[this.contactPersonIndex].phone && this.inputContactPerson[this.contactPersonIndex].email && this.inputContactPerson[this.contactPersonIndex].designation) {
                return true;
            } else {
                return false;
            }
        },
        addMoreContactPerson() {
            const isValid = this.validateContactPerson();
            if (isValid) {
                const obj = {
                    name: "",
                    phone: "",
                    email: "",
                    designation: "",
                }
                this.inputContactPerson.push(obj);
                this.contactPersonIndex = this.contactPersonIndex + 1;
            } else {
                this.contactPersonErrorMsg = "You must fill all the details.";
            }
        },
        removeContactPerson(index) {
            this.inputContactPerson.splice(index, 1);
            this.contactPersonIndex = this.contactPersonIndex - 1;
        },
        validateForm() {
            this.formIsValid = true;
            if (this.mode == 'edit') {
                if (this.logoPicture.val === null && this.logoPreview === null) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = "You must upload a logo.";
                    this.formIsValid = false;
                }
            } else {
                if (this.logoPicture.val === null) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = "You must upload a logo.";
                    this.formIsValid = false;
                }
            }
            if (this.name.val === "") {
                this.name.isValid = false;
                this.name.msg = "Name must not be empty.";
                this.formIsValid = false;
            }

            if (this.selectedEntities.length <= 0) {
                this.entity.isValid = false;
                this.entity.msg = "You must select any of the option.";
                this.formIsValid = false;
            } else {
                this.entity.val = this.selectedEntities.join(',');
            }
            if (this.industry.val === "" || this.industry.val === null) {
                this.industry.isValid = false;
                this.industry.msg = "Please select an industry.";
                this.formIsValid = false;
            }
            if (this.industry.val === "other") {
                if (!this.industryOther.val || this.industryOther.val === "") {
                    this.industryOther.isValid = false;
                    this.industryOther.msg = "Please enter the industry name.";
                    this.formIsValid = false;
                } else if (!this.industryOther.isValid) {
                    this.formIsValid = false;
                }
            }
            if (this.inputContactPerson.length > 1) {
                const isValid = this.validateContactPerson();
                if (!isValid) {
                    this.contactPersonErrorMsg = "You must fill all the details.";
                    this.formIsValid = false;
                }
            } else {
                const isValid = this.validateContactPerson();
                if (!isValid) {
                    this.contactPersonErrorMsg = "You must enter at least 1 contact person.";
                    this.formIsValid = false;
                }
            }

            this.jointCommitteeErrorMsg = "";
            if (!this.validateJointCommitteeMembers()) {
                this.jointCommitteeErrorMsg = "Please complete all joint committee member fields, or remove incomplete rows.";
                this.formIsValid = false;
            }

            this.accountManagersErrorMsg = "";
            if (!this.validateAccountManagers()) {
                this.accountManagersErrorMsg = "Please complete all account manager fields, or remove incomplete rows.";
                this.formIsValid = false;
            }

            this.validateOptionalAttachments();
        },
        validateOptionalAttachments() {
            this.attachmentErrorMsg = "";
            if (!this.inputAttachments.length) {
                return;
            }
            var i;
            for (i = 0; i < this.inputAttachments.length; i++) {
                var attachment = this.inputAttachments[i];
                if (!attachment) {
                    continue;
                }
                if (attachment.is_new) {
                    var hasFile = attachment.document && attachment.document.val != null;
                    var hasName = !!attachment.file_name;
                    if (!hasFile || !hasName) {
                        this.attachmentErrorMsg = "Please complete all document fields, or remove incomplete rows.";
                        this.formIsValid = false;
                        if (attachment.document) {
                            attachment.document.isValid = hasFile;
                            attachment.document.msg = hasFile ? "" : "Document is required";
                        }
                    } else if (attachment.document) {
                        attachment.document.isValid = true;
                        attachment.document.msg = "";
                    }
                } else if (!attachment.file_name) {
                    this.attachmentErrorMsg = "Please complete all document fields, or remove incomplete rows.";
                    this.formIsValid = false;
                }
            }
        },
        validateJointCommitteeMembers() {
            var list = this.inputJointCommitteeMembers || [];
            if (list.length === 0) {
                return true;
            }
            for (var i = 0; i < list.length; i++) {
                var row = list[i];
                if (!row) {
                    return false;
                }
                if (!row.name || !row.title || !row.entity || !row.mobile || !row.email) {
                    return false;
                }
                if (row.member_type === "internal" && !row.user_id) {
                    return false;
                }
            }
            return true;
        },
        validateAccountManagers() {
            var list = this.inputAccountManagers || [];
            if (list.length === 0) {
                return true;
            }
            for (var i = 0; i < list.length; i++) {
                var row = list[i];
                if (!row) {
                    return false;
                }
                if (!row.user_id || !row.name || !row.title || !row.entity || !row.mobile || !row.email) {
                    return false;
                }
            }
            return true;
        },
        buildJointCommitteePayload() {
            var list = this.inputJointCommitteeMembers || [];
            var out = [];
            for (var i = 0; i < list.length; i++) {
                var row = list[i];
                if (!row || !row.name) {
                    continue;
                }
                out.push({
                    user_id: row.member_type === "internal" && row.user_id ? parseInt(row.user_id, 10) : null,
                    name: row.name,
                    title: row.title,
                    entity: row.entity,
                    mobile: row.mobile,
                    email: row.email,
                });
            }
            return out;
        },
        buildAccountManagersPayload() {
            var list = this.inputAccountManagers || [];
            var out = [];
            for (var i = 0; i < list.length; i++) {
                var row = list[i];
                if (!row || !row.user_id || !row.name) {
                    continue;
                }
                out.push({
                    user_id: parseInt(row.user_id, 10),
                    name: row.name,
                    title: row.title,
                    entity: row.entity,
                    mobile: row.mobile,
                    email: row.email,
                });
            }
            return out;
        },
        async submitForm() {
            this.validateForm();

            if (!this.formIsValid) {
                return;
            }

            let modifiedInputAttachments = this.inputAttachments.filter(item => item.document.val != null).map(item => {
                return {
                    id: parseInt(item.id),
                    file_name: item.file_name,
                    document: item.document.val
                };
            });

            const parentPartnerId = this.isPartOfGroupCompany === 'yes' && this.selectedParentPartner
                ? parseInt(this.selectedParentPartner.id, 10)
                : null;

            var industryId = this.industry.val === "other" ? 0 : parseInt(this.industry.val, 10);
            var industryOtherVal = this.industry.val === "other" ? this.industryOther.val : null;

            const formData = {
                logo_picture: this.logoPicture.val,
                logo: this.mode == 'create' ? '' : this.selectedItem.logo,
                name: this.name.val,
                entity: this.entity.val,
                location: this.location.val ? this.location.val : null,
                industry: industryId,
                industry_other: industryOtherVal,
                type_of_collaboration: this.selectedTypeOfCollaboration || null,
                classifications: this.selectedClassifications && this.selectedClassifications.length > 0
                    ? this.selectedClassifications
                    : null,
                parent_partner_id: parentPartnerId,
                input_contact_person: this.inputContactPerson,
                input_joint_committee_members: this.buildJointCommitteePayload(),
                input_account_managers: this.buildAccountManagersPayload(),
                input_attachments: modifiedInputAttachments,
                deleted_attachment_ids: this.deletedAttachmentIds,
                is_pipeline: false,
                show_on_landing_page: this.showOnLandingPage,
            };

            const CREATE_MUTATION = gql`
                mutation CreateCurrentPartner($input: CurrentPartnerInput!) {
                    createCurrentPartner(input: $input) {
                        error
                        message
                    }
                }
            `;

            const UPDATE_MUTATION = gql`
                mutation UpdateCurrentPartner($token: String!, $input: CurrentPartnerInput!) {
                    updateCurrentPartner(token: $token, input: $input) {
                        error
                        message
                    }
                }
            `;

            this.isLoading = true;
            let error_msg = null;
            let success_msg = null;

            try {
                await this.$apollo.mutate({
                    mutation: this.mode == 'create' ? CREATE_MUTATION : UPDATE_MUTATION,
                    variables: {
                        token: this.mode == 'edit' ? this.selectedItem.token : null,
                        input: formData
                    },
                    context: {
                        headers: {
                            'content-type': 'multipart/form-data',
                        },
                    },
                }).then((data) => {
                    if (this.mode == 'create') {
                        if (data.data.createCurrentPartner.error) {
                            error_msg = data.data.createCurrentPartner.message;
                        } else {
                            success_msg = data.data.createCurrentPartner.message;
                        }
                    } else {
                        if (data.data.updateCurrentPartner.error) {
                            error_msg = data.data.updateCurrentPartner.message;
                        } else {
                            success_msg = data.data.updateCurrentPartner.message;
                        }
                    }
                }).catch((error) => {
                    error_msg = error;
                })

            } catch (error) {
                error_msg = error.message || "Failed to authenticate, try later.";
            }

            if (error_msg === null) {
                if (this.mode == 'create') {
                    this.$emit('created', success_msg);
                } else {
                    this.$emit('updated', success_msg);
                }
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: error_msg,
                }).then((result) => {
                    if (result.value || result.dismiss) {
                        this.$emit('close');
                    }
                })
            }

            this.isLoading = false;

        },
        initializeVariables() {
            const obj1 = {
                name: "",
                phone: "",
                email: "",
                designation: "",
            }
            this.inputContactPerson.push(obj1);
            this.inputAttachments = [];
            this.attachmentIndex = -1;
        },
        setFormData() {
            if (this.selectedItem.logo) {
                this.logoPreview = process.env.VUE_APP_PUBLIC_URL + this.selectedItem.logo;
            }
            this.name.val = this.selectedItem.name;
            this.location.val = this.selectedItem.location ? this.selectedItem.location : "";
            this.selectedEntities = this.selectedItem.entity.split(',');
            this.entity.val = this.selectedItem.entity;
            this.industry.val = this.selectedItem.industry ? this.selectedItem.industry : "";
            if (this.selectedItem.show_on_landing_page === false) {
                this.showOnLandingPage = false;
            } else {
                this.showOnLandingPage = true;
            }
            this.selectedTypeOfCollaboration = this.selectedItem.type_of_collaboration
                ? this.selectedItem.type_of_collaboration.split(",")[0].trim()
                : "";
            if (this.selectedItem.classifications && this.selectedItem.classifications.length > 0) {
                this.selectedClassifications = this.selectedItem.classifications.slice();
            } else {
                this.selectedClassifications = [];
            }
            this.isPartOfGroupCompany = this.selectedItem.parent_partner_id ? 'yes' : '';
            if (this.selectedItem.parent_partner_id) {
                const found = this.partnersForSelect.find(
                    p => p.id === this.selectedItem.parent_partner_id
                );
                if (found) {
                    this.selectedParentPartner = found;
                } else if (this.selectedItem.parent_partner && this.selectedItem.parent_partner.id) {
                    const parentObj = {
                        id: this.selectedItem.parent_partner.id,
                        name: this.selectedItem.parent_partner.name,
                        token: this.selectedItem.parent_partner.token,
                        is_pipeline: this.selectedItem.parent_partner.is_pipeline,
                    };
                    this.selectedParentPartner = parentObj;
                    this.partnersForSelect.push(parentObj);
                } else {
                    this.selectedParentPartner = null;
                }
            } else {
                this.selectedParentPartner = null;
            }
            if (this.selectedItem.contact_person.length > 0) {
                this.contactPersonIndex = this.selectedItem.contact_person.length - 1;
                this.selectedItem.contact_person.forEach(cp => {
                    const { name, phone, email, designation } = cp;
                    this.inputContactPerson.push({ name, phone, email, designation });
                });
            } else {
                const obj1 = {
                    name: "",
                    phone: "",
                    email: "",
                    designation: "",
                }
                this.inputContactPerson.push(obj1);
            }

            this.inputJointCommitteeMembers = [];
            if (this.selectedItem.joint_committee_members && this.selectedItem.joint_committee_members.length > 0) {
                for (var ji = 0; ji < this.selectedItem.joint_committee_members.length; ji++) {
                    var jm = this.selectedItem.joint_committee_members[ji];
                    this.inputJointCommitteeMembers.push({
                        member_type: jm.user_id ? "internal" : "external",
                        user_id: jm.user_id ? parseInt(jm.user_id, 10) : null,
                        user_search: (jm.user && jm.user.name) ? jm.user.name : (jm.name || ""),
                        user_results: [],
                        show_user_dropdown: false,
                        search_timer: null,
                        name: jm.name || "",
                        title: jm.title || "",
                        entity: jm.entity || "",
                        mobile: jm.mobile || "",
                        email: jm.email || "",
                    });
                }
            }

            this.inputAccountManagers = [];
            if (this.selectedItem.account_managers && this.selectedItem.account_managers.length > 0) {
                for (var ai = 0; ai < this.selectedItem.account_managers.length; ai++) {
                    var am = this.selectedItem.account_managers[ai];
                    this.inputAccountManagers.push({
                        user_id: am.user_id ? parseInt(am.user_id, 10) : null,
                        user_search: (am.user && am.user.name) ? am.user.name : (am.name || ""),
                        user_results: [],
                        show_user_dropdown: false,
                        search_timer: null,
                        name: am.name || "",
                        title: am.title || "",
                        entity: am.entity || "",
                        mobile: am.mobile || "",
                        email: am.email || "",
                    });
                }
            }

            if (this.selectedItem.attachments && this.selectedItem.attachments.length > 0) {
                this.attachmentIndex = this.selectedItem.attachments.length - 1;
                this.selectedItem.attachments.forEach(cp => {
                    const obj3 = {
                        is_new: false,
                        id: cp.id,
                        original_filename: cp.original_filename,
                        path: cp.path,
                        file_name: cp.file_name,
                        document: {
                            val: null,
                            isValid: true,
                            msg: "",
                        }
                    }
                    this.inputAttachments.push(obj3);
                });
            } else {
                this.inputAttachments = [];
                this.attachmentIndex = -1;
            }
        },
        onPickFile(ref) {
            this.$refs[ref].click();
        },
        handleUploadedPhoto(event) {
            this.logoPicture.isValid = true;
            this.logoPicture.msg = "";
            const file = event.target.files[0];
            if (file) {
                if (!file.type.includes('image/')) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = 'Only JPG, JPEG and PNG files are allowed.';
                } else if (file.size > 2 * 1024 * 1024) {
                    this.logoPicture.isValid = false;
                    this.logoPicture.msg = 'File size must be less than 2MB.';
                } else {
                    this.logoPicture.isValid = true;
                    this.logoPicture.msg = '';
                    this.logoPicture.val = file;
                    this.logoPreview = URL.createObjectURL(file);
                }
            }
        },
        cancelUploadedPhoto() {
            this.logoPicture.val = null;
            this.logoPreview = null;
        },
        handleFileChange(event) {
            const fileIndex = parseInt(event.target.id.split('-').pop());
            const targetIndex = isNaN(fileIndex) ? this.attachmentIndex : fileIndex;

            this.inputAttachments[targetIndex].document.isValid = true;
            this.inputAttachments[targetIndex].document.msg = "";
            const file = event.target.files[0];

            if (file) {
                if (!this.isValidFileType(file)) {
                    this.inputAttachments[targetIndex].document.isValid = false;
                    this.inputAttachments[targetIndex].document.msg = 'Only JPG, JPEG, PNG, PDF, DOC, DOCX, PPT, and PPTX files are allowed.';
                } else if (file.size > 16 * 1024 * 1024) {
                    this.inputAttachments[targetIndex].document.isValid = false;
                    this.inputAttachments[targetIndex].document.msg = 'File size must be less than 16MB.';
                } else {
                    this.inputAttachments[targetIndex].document.isValid = true;
                    this.inputAttachments[targetIndex].document.msg = '';
                    this.inputAttachments[targetIndex].document.val = file;
                    this.inputAttachments[targetIndex].document.is_new = true;
                    this.inputAttachments[targetIndex].document.id = null;
                    this.inputAttachments[targetIndex].document.original_filename = "";
                    this.inputAttachments[targetIndex].document.path = "";
                }
            }

        },
    },
}
</script>

<style lang="scss" scoped>
.form-shell {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;
}

.form-body {
    width: 100%;
    max-width: 58rem;
    margin: 0 auto;
    padding: 1.25rem 1.25rem 1.5rem;
}

@media (min-width: 768px) {
    .form-body {
        padding: 2rem 2rem 1.75rem;
    }
}

.form-section {
    @apply mb-6;
    padding: 1.35rem 1.5rem 1.5rem;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.section-header {
    @apply flex items-center gap-3 mb-5 pb-4;
    border-bottom: 1px solid #f1f5f9;
}

.section-header-with-meta {
    align-items: flex-start;
}

.section-heading-text {
    min-width: 0;
    flex: 1;
}

.section-title-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.section-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.4rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #0c4a6e;
    background: #e0f2fe;
}

.section-subtitle {
    margin: 0.2rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #64748b;
}

.section-icon {
    @apply flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0;
    background: #e0f2fe;
    color: #0c4a6e;
}

.section-title {
    @apply text-lg font-semibold text-slate-800;
    letter-spacing: -0.01em;
    margin: 0;
}

/* Logo Upload */
.logo-upload-card {
    @apply flex flex-col sm:flex-row items-start gap-5 mb-6;
    padding: 1.15rem;
    border-radius: 0.85rem;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border: 1px solid #eef2f7;
}

.landing-page-display-group {
    margin-bottom: 1.5rem;
}

.landing-page-display-option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}

.landing-page-display-option:hover {
    border-color: #cbd5e1;
    background: #f1f5f9;
}

.landing-page-display-checkbox {
    margin-top: 0.2rem;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    accent-color: #0284c7;
}

.landing-page-display-text {
    font-size: 0.875rem;
    line-height: 1.5;
    color: #475569;
}

.logo-preview-wrapper {
    @apply relative inline-block;
}

.logo-remove-btn {
    @apply absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full cursor-pointer;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    transition: all 0.2s ease;
}

.logo-remove-btn:hover {
    background: #fee2e2;
}

.logo-preview-img {
    @apply h-36 w-36 object-contain rounded-xl;
    border: 1px solid #e2e8f0;
    background: #fff;
}

.logo-placeholder {
    @apply flex flex-col items-center justify-center w-36 h-36 rounded-xl;
    background: #fff;
    border: 2px dashed #cbd5e1;
}

.logo-placeholder-icon {
    @apply mb-2;
}

.logo-placeholder-text {
    @apply text-xs text-slate-500 px-2 text-center;
}

.logo-upload-btn {
    @apply flex flex-col items-center gap-1 px-5 py-3 rounded-lg cursor-pointer transition-all duration-200;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.logo-upload-btn:hover {
    background: #f0f9ff;
    color: #0c4a6e;
    border-color: #7dd3fc;
}

.logo-upload-btn-error {
    border-color: #fecaca;
    color: #dc2626;
    background: #fef2f2;
}

.logo-upload-hint {
    @apply text-xs text-slate-500;
}

/* Form Grid */
.form-grid {
    @apply grid grid-cols-1 sm:grid-cols-2 gap-5;
}

.form-group {
    @apply flex flex-col gap-1.5;
}

.form-group-error .form-label {
    color: #dc2626;
}

.form-group-error .form-input,
.form-group-error .form-select {
    border-color: #fca5a5;
    box-shadow: 0 0 0 1px #fecaca;
}

.form-label {
    @apply text-sm font-medium text-slate-700;
}

.required {
    color: #dc2626;
}

.form-input,
.form-select {
    @apply block w-full rounded-lg py-2.5 px-3 text-sm;
    border: 1px solid #e2e8f0;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.form-input::placeholder {
    color: #94a3b8;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.form-input-disabled {
    background: #f8fafc;
    color: #64748b;
}

.form-error-msg {
    @apply text-xs text-rose-600 mt-0.5;
}

.form-error-msg-center {
    @apply text-center mt-2;
}

/* Entity selection */
.entity-group {
    @apply mt-5;
}

.entity-panel {
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 55%);
    padding: 1rem 1.1rem 1.1rem;
}

.entity-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.9rem;
}

.entity-panel-heading {
    min-width: 0;
}

.entity-panel-label {
    margin-bottom: 0.2rem;
}

.entity-panel-hint {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: #64748b;
}

.entity-selected-count {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.6rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #0c4a6e;
    background: #e0f2fe;
}

.entity-chip-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
}

@media (min-width: 640px) {
    .entity-chip-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (min-width: 1024px) {
    .entity-chip-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }
}

.entity-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 2.75rem;
    padding: 0.65rem 0.75rem;
    border-radius: 0.65rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.15s ease;
    user-select: none;
}

.entity-chip:hover {
    border-color: #7dd3fc;
    background: #f0f9ff;
}

.entity-chip--selected {
    border-color: #0284c7;
    background: #e0f2fe;
    box-shadow: 0 0 0 1px rgba(2, 132, 199, 0.2);
}

.entity-chip--selected:hover {
    background: #e0f2fe;
    border-color: #0284c7;
}

.entity-chip-input {
    @apply sr-only;
}

.entity-chip-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.15rem;
    height: 1.15rem;
    flex-shrink: 0;
    border-radius: 0.3rem;
    border: 1.5px solid #cbd5e1;
    background: #fff;
    color: #fff;
    font-size: 0.6rem;
    transition: all 0.15s ease;
}

.entity-chip--selected .entity-chip-check {
    border-color: #0c4a6e;
    background: #0c4a6e;
}

.entity-chip-name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    letter-spacing: 0.01em;
}

.entity-chip--selected .entity-chip-name {
    color: #0c4a6e;
}

.entity-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.9rem;
    padding-top: 0.85rem;
    border-top: 1px solid #eef2f7;
}

.entity-summary-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    margin-right: 0.15rem;
}

.entity-summary-tag {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.55rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #0c4a6e;
    background: #fff;
    border: 1px solid #bae6fd;
}

.form-group-error .entity-panel {
    border-color: #fca5a5;
    box-shadow: 0 0 0 1px #fecaca;
}

/* Repeatable cards (contacts, documents) */
.repeatable-list {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.repeatable-card {
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.repeatable-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #f8fafc;
    border-bottom: 1px solid #eef2f7;
}

.repeatable-card-header-left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.repeatable-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.85rem;
    height: 1.85rem;
    border-radius: 0.45rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #0c4a6e;
    background: #e0f2fe;
    flex-shrink: 0;
}

.repeatable-card-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
}

.repeatable-btn-remove {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.7rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #b91c1c;
    background: #fff;
    border: 1px solid #fecaca;
    border-radius: 0.45rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.repeatable-btn-remove:hover {
    background: #fef2f2;
    border-color: #fca5a5;
}

.repeatable-btn-remove-icon {
    font-size: 0.7rem;
}

.repeatable-card-body {
    padding: 1rem;
}

.contact-person-fields {
    @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4;
}

.document-fields {
    @apply grid grid-cols-1 sm:grid-cols-2 gap-4;
}

.repeatable-footer {
    display: flex;
    justify-content: center;
    padding-top: 0.15rem;
}

.repeatable-btn-add {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.65rem 1.25rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0c4a6e;
    background: #fff;
    border: 1px dashed #7dd3fc;
    border-radius: 0.55rem;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.repeatable-btn-add:hover {
    border-style: solid;
    border-color: #0284c7;
    background: #f0f9ff;
    color: #075985;
}

/* Optional documents empty state */
.docs-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2.25rem 1.5rem;
    border-radius: 0.75rem;
    border: 1px dashed #bae6fd;
    background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%);
}

.docs-empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    margin-bottom: 0.75rem;
    border-radius: 0.75rem;
    background: #e0f2fe;
    color: #0c4a6e;
    font-size: 1.15rem;
}

.docs-empty-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1e293b;
}

.docs-empty-text {
    margin: 0.35rem 0 1.1rem;
    max-width: 22rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #64748b;
}

.docs-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.6rem 1.15rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: #0c4a6e;
    border: none;
    border-radius: 0.55rem;
    cursor: pointer;
    box-shadow: 0 6px 16px -8px rgba(12, 74, 110, 0.5);
    transition: background 0.2s, transform 0.15s;
}

.docs-btn-primary:hover {
    background: #075985;
    transform: translateY(-1px);
}

/* Document existing file */
.document-existing {
    @apply flex items-center gap-2 p-3 rounded-lg mt-6;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.document-link {
    @apply text-sm text-twelve hover:underline truncate flex-1 min-w-0;
}

.document-remove-btn {
    @apply flex items-center justify-center w-8 h-8 rounded cursor-pointer text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors;
}

.file-input-wrapper {
    @apply flex flex-col gap-1;
}

.file-input {
    display: block;
    width: 100%;
    font-size: 0.875rem;
    color: #475569;
}

.file-input::-webkit-file-upload-button {
    margin-right: 1rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 0;
    font-size: 0.875rem;
    font-weight: 500;
    background: #f1f5f9;
    color: #334155;
    cursor: pointer;
}

.file-input::-webkit-file-upload-button:hover {
    background: #e2e8f0;
}

.file-input::file-selector-button {
    margin-right: 1rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    border: 0;
    font-size: 0.875rem;
    font-weight: 500;
    background: #f1f5f9;
    color: #334155;
    cursor: pointer;
}

.file-input::file-selector-button:hover {
    background: #e2e8f0;
}

.file-input-hint {
    @apply text-xs text-slate-500;
}

/* Form Footer — sticky within modal scroll */
.form-footer {
    position: sticky;
    bottom: 0;
    z-index: 40;
    width: 100%;
    margin-top: auto;
    background: rgba(255, 255, 255, 0.97);
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -10px 28px -16px rgba(15, 23, 42, 0.18);
}

.form-footer-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    max-width: 58rem;
    margin: 0 auto;
    padding: 0.85rem 1.25rem;
}

@media (min-width: 768px) {
    .form-footer-inner {
        padding: 0.95rem 2rem;
    }
}

.form-footer-loading {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    margin-left: auto;
    padding: 0.35rem 0;
}

.cancel-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 6.5rem;
    padding: 0.7rem 1.15rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #475569;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 0.65rem;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.cancel-btn:hover {
    background: #f8fafc;
    border-color: #94a3b8;
    color: #0f172a;
}

.submit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 9.5rem;
    padding: 0.7rem 1.35rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: #0c4a6e;
    border: none;
    border-radius: 0.65rem;
    cursor: pointer;
    box-shadow: 0 8px 18px -10px rgba(12, 74, 110, 0.5);
    transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
}

.submit-btn:hover {
    background: #075985;
    transform: translateY(-1px);
    box-shadow: 0 10px 22px -10px rgba(12, 74, 110, 0.55);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    height: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: all 0.3s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
    height: 0;
}

.form-group-multiselect :deep(.multiselect__tags) {
    min-height: 2.625rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    padding-top: 0.35rem;
}

.form-group-multiselect :deep(.multiselect__input),
.form-group-multiselect :deep(.multiselect__single) {
    font-size: 0.875rem;
}

.form-group-multiselect :deep(.multiselect--active .multiselect__tags) {
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.option__desc {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.option__title {
    font-weight: 500;
}

.option__small {
    font-size: 0.75rem;
    color: #64748b;
}
</style>
